import { useState } from 'react';
import { Redirect } from 'react-router';
import { useParams } from 'react-router-dom-v5-compat';
import { useGraphql } from 'utils/hooks/useGraphql';
import { Banner } from 'ui/banner';
import { BannerVariants } from 'ui/banner/types';
import noop from 'utils/noop';
import { fetchPredictiveAudienceGoalQuery } from 'api/queries/predictiveAudiences';
import { RUMHelpers } from 'services/observability/datadog';
import { ReadOnlyGoalSkeleton } from 'modules/predictiveGoals/components/ReadOnlyGoalSkeleton';
import { createActionName } from '../observabilityUtils';
import { GoalGraphContextProvider } from './contexts/GoalGraphContextProvider';
import { PredictiveGoalsHeader } from './PredictiveGoalsHeader';
import { PredictiveAudienceGoal, GoalStatus, EditPredictiveGoalVariables } from '../types';
import { MainLayoutWrapper, MainContentLayout } from './Styled';
import { InProgressStatus } from './InProgressStatus';
import { ResultsPage } from './ready/ResultsPage';
import { DeletingStatus } from './DeletingStatus';
import { hasErrorStatus, toGoalVariables } from '../goalUtils';

export const ReadOnlyGoalPage = () => {
  const { goalId: goalIdStr } = useParams<{ goalId: string }>();
  const goalId = parseInt(goalIdStr as string, 10);
  const [goal, setGoal] = useState<PredictiveAudienceGoal>();
  const [goalName, setGoalName] = useState('');

  const { isLoading: loadingGoal } = useGraphql(fetchPredictiveAudienceGoalQuery, {
    variables: { id: goalId },
    onCompleted: ({ predictiveAudienceGoal = {} }) => {
      setGoal(predictiveAudienceGoal);
      setGoalName(predictiveAudienceGoal.name);
    },
    onError: () => {
      // Ensure an error is displayed
      setGoal({} as any);
    },
  });

  if (loadingGoal || goal === undefined) {
    return (
      <>
        <PredictiveGoalsHeader
          isReadOnly
          editableGoal={{} as EditPredictiveGoalVariables}
          goalName={''}
          onUpdateGoalName={noop}
          isLoading
        />
        <MainLayoutWrapper>
          <MainContentLayout>
            <ReadOnlyGoalSkeleton />
          </MainContentLayout>
        </MainLayoutWrapper>
      </>
    );
  }

  const goalVariables = toGoalVariables(goal);

  if (hasErrorStatus(goal)) {
    RUMHelpers.addRUMAction(
      createActionName(
        'Redirect to edit page. Goals with status of Needs Review or System Error loaded',
      ),
      { goalStatus: goal?.status },
    );
    return <Redirect to={`/predictive-audiences/goals/${goal.id}/edit`} />;
  }

  const showInProgressStatus = goal.status === GoalStatus.PREPARING;
  const showResultsPage = goal.status === GoalStatus.READY || goal.status === GoalStatus.REFRESHING;
  const showDeletePage = goal.status === GoalStatus.DELETING;

  if (showInProgressStatus) {
    return (
      <>
        <PredictiveGoalsHeader
          isReadOnly
          editableGoal={goalVariables}
          goalName={goalName}
          onUpdateGoalName={setGoalName}
        />
        <InProgressStatus goal={goal} />
      </>
    );
  } else if (showResultsPage) {
    return (
      <GoalGraphContextProvider>
        <ResultsPage goal={goal} onMutation={setGoal} />
      </GoalGraphContextProvider>
    );
  } else if (showDeletePage) {
    return (
      <>
        <PredictiveGoalsHeader
          isReadOnly
          editableGoal={goalVariables}
          goalName={goalName}
          onUpdateGoalName={setGoalName}
        />
        <DeletingStatus goal={goal} />
      </>
    );
  }

  return (
    <GoalGraphContextProvider>
      <PredictiveGoalsHeader
        editableGoal={goalVariables}
        goalName={goalName}
        onUpdateGoalName={setGoalName}
      />
      <MainLayoutWrapper>
        <MainContentLayout>
          <Banner variant={BannerVariants.ERROR}>Unable to load goal!</Banner>
        </MainContentLayout>
      </MainLayoutWrapper>
    </GoalGraphContextProvider>
  );
};
